import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, GripVertical, Save, X, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CollectionManager = () => {
  const [collections, setCollections] = useState([]);
  const [allArtworks, setAllArtworks] = useState([]);
  const [editing, setEditing] = useState(null); // collection being edited
  const [isNew, setIsNew] = useState(false);
  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [colRes, artRes] = await Promise.all([
        axios.get(`${API}/collections?include_artworks=true`),
        axios.get(`${API}/artworks`),
      ]);
      setCollections(colRes.data);
      setAllArtworks(artRes.data);
    } catch {
      toast.error('Failed to load data');
    }
  };

  const handleCreate = () => {
    setEditing({ name: '', description: '', artwork_ids: [], order: collections.length });
    setIsNew(true);
  };

  const handleEdit = (collection) => {
    setEditing({ ...collection });
    setIsNew(false);
  };

  const handleSave = async () => {
    if (!editing.name.trim()) {
      toast.error('Collection name is required');
      return;
    }
    try {
      if (isNew) {
        await axios.post(`${API}/collections`, editing, { headers });
        toast.success('Collection created');
      } else {
        await axios.put(`${API}/collections/${editing.id}`, editing, { headers });
        toast.success('Collection updated');
      }
      setEditing(null);
      setIsNew(false);
      loadData();
    } catch {
      toast.error('Failed to save collection');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this collection?')) return;
    try {
      await axios.delete(`${API}/collections/${id}`, { headers });
      toast.success('Collection deleted');
      loadData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const toggleArtwork = (artworkId) => {
    if (!editing) return;
    const ids = [...(editing.artwork_ids || [])];
    const idx = ids.indexOf(artworkId);
    if (idx >= 0) {
      ids.splice(idx, 1);
    } else {
      ids.push(artworkId);
    }
    setEditing({ ...editing, artwork_ids: ids });
  };

  const moveArtwork = (index, direction) => {
    if (!editing) return;
    const ids = [...(editing.artwork_ids || [])];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= ids.length) return;
    [ids[index], ids[newIndex]] = [ids[newIndex], ids[index]];
    setEditing({ ...editing, artwork_ids: ids });
  };

  return (
    <div className="min-h-screen pt-20 pb-16 px-4 sm:px-6 md:px-12" data-testid="collection-manager">
      <div className="max-w-screen-xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-2xl font-serif font-light">Collections</h1>
          </div>
          <Button onClick={handleCreate} data-testid="create-collection-btn">
            <Plus size={16} className="mr-1" /> New Collection
          </Button>
        </div>

        {/* Editor modal */}
        {editing && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => { setEditing(null); setIsNew(false); }}>
            <div className="bg-card rounded-lg w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="collection-editor">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium">{isNew ? 'New Collection' : 'Edit Collection'}</h2>
                <button onClick={() => { setEditing(null); setIsNew(false); }}><X size={20} /></button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground block mb-1">Name</label>
                  <input
                    type="text"
                    value={editing.name}
                    onChange={e => setEditing({ ...editing, name: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                    placeholder="e.g. Krishna Series"
                    data-testid="collection-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground block mb-1">Description</label>
                  <textarea
                    value={editing.description}
                    onChange={e => setEditing({ ...editing, description: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                    rows={3}
                    placeholder="A brief description of this collection..."
                    data-testid="collection-desc-input"
                  />
                </div>

                {/* Selected artworks (reorderable) */}
                <div>
                  <label className="text-sm text-muted-foreground block mb-2">
                    Selected Artworks ({editing.artwork_ids?.length || 0})
                  </label>
                  {editing.artwork_ids?.length > 0 && (
                    <div className="space-y-1 mb-4 border border-border rounded-md p-2">
                      {editing.artwork_ids.map((aid, idx) => {
                        const art = allArtworks.find(a => a.id === aid);
                        return (
                          <div key={aid} className="flex items-center gap-2 p-1.5 bg-muted/50 rounded text-sm">
                            <div className="flex flex-col gap-0.5">
                              <button onClick={() => moveArtwork(idx, -1)} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30 text-xs">
                                ▲
                              </button>
                              <button onClick={() => moveArtwork(idx, 1)} disabled={idx === editing.artwork_ids.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30 text-xs">
                                ▼
                              </button>
                            </div>
                            <img src={art?.thumb_url || art?.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                            <span className="flex-1 truncate">{art?.title || aid}</span>
                            <button onClick={() => toggleArtwork(aid)} className="text-red-500 hover:text-red-600">
                              <X size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Available artworks */}
                <div>
                  <label className="text-sm text-muted-foreground block mb-2">Add Artworks</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-60 overflow-y-auto border border-border rounded-md p-2">
                    {allArtworks.filter(a => !(editing.artwork_ids || []).includes(a.id)).map(art => (
                      <button
                        key={art.id}
                        onClick={() => toggleArtwork(art.id)}
                        className="group relative aspect-square overflow-hidden rounded border border-border hover:border-accent transition-colors"
                        data-testid={`add-artwork-${art.id}`}
                      >
                        <img src={art.thumb_url || art.image_url} alt={art.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                          <Plus size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5">
                          <p className="text-white text-[10px] truncate">{art.title}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <Button onClick={handleSave} className="flex-1" data-testid="save-collection-btn">
                  <Save size={16} className="mr-1" /> Save Collection
                </Button>
                <Button variant="outline" onClick={() => { setEditing(null); setIsNew(false); }}>Cancel</Button>
              </div>
            </div>
          </div>
        )}

        {/* Collection list */}
        {collections.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <Layers size={48} className="mx-auto mb-4 opacity-40" />
            <p className="text-lg">No collections yet</p>
            <p className="text-sm mt-1">Create your first collection to group artworks.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {collections.map(collection => (
              <div key={collection.id} className="flex items-center gap-4 p-4 bg-card rounded-lg border border-border" data-testid={`collection-item-${collection.id}`}>
                {/* Preview thumbnails */}
                <div className="w-20 h-20 rounded overflow-hidden bg-muted flex-shrink-0 grid grid-cols-2 grid-rows-2">
                  {(collection.artworks || []).slice(0, 4).map(art => (
                    <img key={art.id} src={art.thumb_url || art.image_url} alt="" className="w-full h-full object-cover" />
                  ))}
                  {Array.from({ length: Math.max(0, 4 - (collection.artworks?.length || 0)) }).map((_, i) => (
                    <div key={i} className="bg-muted/50" />
                  ))}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium truncate">{collection.name}</h3>
                  <p className="text-sm text-muted-foreground truncate">{collection.description || 'No description'}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{collection.artwork_ids?.length || 0} artworks</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(collection)} data-testid={`edit-collection-${collection.id}`}>
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(collection.id)} className="text-red-500 hover:text-red-600">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionManager;
